// Documents → Files: the client's Drive folder, read live, one collapsible
// row per folder, and inside it a collapsible row for every folder it holds,
// at any depth (a submission's folder inside PA Submissions, a "Change
// Request" folder inside that) — empty ones too — so it shows everything on
// file the way it is organised, including documents that never passed through
// CAERA. Who uploaded or generated a file shows on
// the file itself (from CAERA's `documents` table); it is never listed apart.
// Nothing to do here except find a file or add one.
import { useCallback, useMemo, useState } from 'react';
import OpenLink from '../OpenLink';
import { FileRow, FolderRow, fmtDay } from '../DriveFolderTree';
import { driveFolderUrl } from '../../lib/driveRoots';
import { driveFileId } from '../../lib/driveLinks';
import { flattenBucket } from '../../lib/driveListing';

// Fax pipeline file names carry the direction: "G005 – 2026-09-10 – SENT – …".
const faxDirection = (name) => (/ – SENT – /.test(name) ? 'Sent' : 'Received');

const st = {
  section: { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--card-bg)', marginBottom: 14 },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' },
};

export default function ClientDocumentsTab({ client, driveTree, driveLoading, driveErr, onRefresh, documents = [], latestAssessment, onUpload }) {
  const [openFolders, setOpenFolders] = useState(() => new Set());
  const toggle = (id) => setOpenFolders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Attribution: documents rows keyed by Drive file id.
  const byDriveId = useMemo(() => {
    const m = new Map();
    documents.forEach((d) => { const k = driveFileId(d.drive_url); if (k && !m.has(k)) m.set(k, d); });
    return m;
  }, [documents]);

  const folders = [...(driveTree?.folders || [])].sort((a, b) => (a.name === 'PA Submissions' ? -1 : b.name === 'PA Submissions' ? 1 : a.name.localeCompare(b.name)));
  const totalFiles = folders.reduce((n, f) => n + flattenBucket(f).filter((x) => !x.mimeType?.includes('folder')).length, 0) + (driveTree?.files?.length || 0);
  const [showLoose, setShowLoose] = useState(false);

  // What a file row adds for a client: who filed it, and a fax's direction.
  const fileNotes = useCallback((f) => {
    const doc = byDriveId.get(f.id);
    return (
      <>
        {doc?.uploaded_by_name && <span style={{ color: 'var(--text2)' }}>· {doc.document_type === 'Signing Packets' || /assessment/i.test(doc.notes || '') ? 'Generated' : 'Uploaded'} by {doc.uploaded_by_name}</span>}
        {doc?.notes && <span style={{ color: 'var(--text2)', fontStyle: 'italic' }}>· {doc.notes}</span>}
      </>
    );
  }, [byDriveId]);
  const fileBadge = useCallback((f, rootName) => {
    if (rootName !== 'Faxes' || f.mimeType?.includes('folder')) return null;
    const sent = faxDirection(f.name) === 'Sent';
    return <span style={{ fontSize: 10, fontWeight: 700, padding: '0 5px', borderRadius: 4, flexShrink: 0, background: sent ? 'var(--bg2)' : 'var(--accent-bg)', color: sent ? 'var(--text2)' : 'var(--accent)', border: '1px solid var(--border)' }}>{faxDirection(f.name)}</span>;
  }, []);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{client.first_name}'s files</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            The Drive folder, read live · 6 – Client Folder / {client.client_id}{driveTree?.folderId ? ` · ${totalFiles} file${totalFiles === 1 ? '' : 's'}` : ''} ·{' '}
            <button type="button" style={st.linkBtn} onClick={onRefresh} disabled={driveLoading}>{driveLoading ? 'Reading…' : 'Refresh'}</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => onUpload(null)}>Upload</button>
          {client.drive_folder_id && <OpenLink href={driveFolderUrl(client.drive_folder_id)}>Open in Drive</OpenLink>}
        </div>
      </div>

      <div style={{ ...st.section, marginBottom: 0 }}>

        {driveErr && <div style={{ color: 'var(--danger)', fontSize: 12.5, padding: '12px 16px' }}>{driveErr}</div>}
        {!driveErr && driveLoading && !driveTree && <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>Reading the Drive folder…</div>}
        {!driveErr && driveTree && !driveTree.folderId && (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>No Drive folder could be found or created for this client — try Refresh, and if it persists the Drive connection needs looking at.</div>
        )}

        {driveTree?.folderId && folders.length === 0 && !driveTree.files?.length && (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>The folder is empty. Subfolders appear as documents are filed — or upload the first one now.</div>
        )}
        {folders.map((folder) => (
          <FolderRow key={folder.id} folder={folder} open={openFolders.has(folder.id)} onToggle={() => toggle(folder.id)}
            fileNotes={fileNotes} fileBadge={fileBadge} onUpload={onUpload}
            note={folderNote(folder.name, latestAssessment)} />
        ))}

        {driveTree?.files?.length > 0 && (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 16px', fontSize: 12.5, color: 'var(--warn)', background: 'var(--warn-bg)' }}>
              <span>⚠</span>
              <span style={{ flex: 1 }}>{driveTree.files.length} file{driveTree.files.length === 1 ? ' sits' : 's sit'} loose in the client folder, not in any subfolder.</span>
              <button type="button" style={{ ...st.linkBtn, color: 'var(--warn)' }} onClick={() => setShowLoose((v) => !v)}>{showLoose ? 'Hide' : 'Show them'}</button>
            </div>
            {showLoose && driveTree.files.map((f) => <FileRow key={f.id} file={f} fileNotes={fileNotes} fileBadge={fileBadge} />)}
          </div>
        )}
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>
        Clinical &amp; progress notes and visit reports are kept in <b style={{ color: 'var(--text2)' }}>AxisCare</b> (Care Notes), signed and dated by care staff — Rule 111-8-65-.09(4)(a)(4).
      </div>
    </div>
  );
}

// A line under the file list that says where a folder's contents come from.
function folderNote(name, latestAssessment) {
  switch (name) {
    case 'Assessments':
      return latestAssessment
        ? <>Written in the <b>Clinical</b> tab; latest assessment {fmtDay(latestAssessment.updated_at)} — <OpenLink href={latestAssessment.drive_document_url} className="btn-ghost" style={{ padding: '0 4px' }}>View</OpenLink></>
        : <>Assessments are written in the <b>Clinical</b> tab; the finished documents land here.</>;
    case 'Faxes': return <>Received faxes are filed here automatically. Sent letters are in <b>Correspondence</b>.</>;
    case 'Signing Packets': return <>Older signed packets (SignWell era). New requests file under <b>PA Submissions</b>.</>;
    case 'PA Submissions': return <>One folder per submission to DCH — the signed forms, the supporting notes, the certificate, the LON.</>;
    case 'Supervisor Visits': return <>Written under <b>Clinical → Supervision</b>.</>;
    default: return null;
  }
}
